import type { ReactNode } from "react";
import { Check } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  optionCardClassName,
  optionLabelId,
} from "@/views/shop/sections/configurator/OptionCard";

/**
 * An add-on card drawn as one checkbox (/product): the whole card selects,
 * and the box at its start shows the state, so selected reads as a check and
 * a heavier border, never colour alone. Details open from the group's
 * "Learn more", never from a card, so reading never toggles a selection.
 *
 * Named by the card's own label, so the control needs no text of its own. A
 * locked card (included by a package) shows checked and takes no clicks; an
 * unavailable one is dimmed as well.
 */
export const OptionCheckCard = ({
  isSelected,
  isLocked,
  isUnavailable,
  descriptionId,
  onToggle,
  children,
}: {
  isSelected: boolean;
  isLocked: boolean;
  isUnavailable: boolean;
  /** OptionCardBody's description id; its label id derives from it. */
  descriptionId: string;
  onToggle: () => void;
  children: ReactNode;
}) => {
  const isChecked = isLocked || isSelected;

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={isChecked}
      aria-labelledby={optionLabelId(descriptionId)}
      aria-describedby={descriptionId}
      disabled={isLocked || isUnavailable}
      onClick={onToggle}
      className={cn(
        optionCardClassName(isChecked, isUnavailable),
        "group flex-row items-start gap-m",
        isLocked && "cursor-default",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "mt-[2px] flex size-l shrink-0 items-center justify-center rounded-4 border transition-colors duration-quick motion-reduce:transition-none",
          isChecked
            ? "border-brand-black bg-brand-black text-brand-white"
            : "border-tint-black-70 bg-brand-white [@media(hover:hover)]:group-hover:border-brand-black",
        )}
      >
        {/* The tick scales in from 0.9, so the check lands rather than
            appears. */}
        {isChecked && (
          <Check
            className="size-m animate-tick-in motion-reduce:animate-none"
            strokeWidth={2.5}
          />
        )}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-xs">{children}</span>
    </button>
  );
};
